import { ROLE_META } from "@/lib/meta";
import { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import { tenantColor } from "@/lib/meta";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ShieldCheck, ShieldOff, Loader2, Crown } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const ROLES = ["admin", "manager", "hire_manager", "hire_coordinator", "service_technician", "job_dispatcher", "rigger", "viewer"];
const empty = { name: "", email: "", password: "", memberships: {} };
const saEmpty = { name: "", email: "", password: "" };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [reset2faId, setReset2faId] = useState(null);

  // super admin accounts
  const [supers, setSupers] = useState([]);
  const [saOpen, setSaOpen] = useState(false);
  const [saEditId, setSaEditId] = useState(null);
  const [saForm, setSaForm] = useState(saEmpty);
  const [saBusy, setSaBusy] = useState(false);
  const [saDeleteId, setSaDeleteId] = useState(null);

  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  const loadSupers = useCallback(() => api.get("/admin/superadmins").then((r) => setSupers(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    loadSupers();
    api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
  }, [load, loadSupers]);

  // Group tenant accounts by person (email) so each shows once with a combined tenant badge.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const u of users) {
      if (!map.has(u.email)) map.set(u.email, { ...u, memberships: [] });
      const g = map.get(u.email);
      g.memberships.push({
        id: u.id, tenant_id: u.tenant_id, tenant_name: u.tenant_name, role: u.role,
        twofa_enabled: u.twofa_enabled, twofa_method: u.twofa_method,
        twofa_required: u.twofa_required, twofa_allowed: u.twofa_allowed,
      });
    }
    return Array.from(map.values());
  }, [users]);

  const openSaCreate = () => { setSaEditId(null); setSaForm(saEmpty); setSaOpen(true); };
  const openSaEdit = (a) => { setSaEditId(a.id); setSaForm({ name: a.name, email: a.email, password: "" }); setSaOpen(true); };

  const saveSa = async (e) => {
    e.preventDefault();
    setSaBusy(true);
    try {
      if (saEditId) {
        const payload = { name: saForm.name, email: saForm.email };
        if (saForm.password) payload.password = saForm.password;
        await api.put(`/admin/superadmins/${saEditId}`, payload);
        toast.success("Super admin updated");
      } else {
        await api.post("/admin/superadmins", saForm);
        toast.success("Super admin added");
      }
      setSaOpen(false);
      loadSupers();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaBusy(false);
    }
  };

  const doSaDelete = async () => {
    try { await api.delete(`/admin/superadmins/${saDeleteId}`); toast.success("Super admin deleted"); loadSupers(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaDeleteId(null); }
  };

  const [saReset2faId, setSaReset2faId] = useState(null);
  const doSaReset2fa = async () => {
    try { await api.post(`/admin/superadmins/${saReset2faId}/2fa/reset`); toast.success("2FA reset — they must set it up again at next login"); loadSupers(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaReset2faId(null); }
  };

  const openCreate = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (g) => {
    setEditId(g.id);
    // Map every tenant membership to its per-tenant role.
    const memberships = {};
    (g.memberships || []).forEach((m) => { if (m.tenant_id) memberships[m.tenant_id] = m.role; });
    setForm({ name: g.name, email: g.email, password: "", memberships });
    setOpen(true);
  };

  const addTenant = (tid) =>
    setForm((f) => (tid in f.memberships ? f : { ...f, memberships: { ...f.memberships, [tid]: "manager" } }));

  const removeTenant = (tid) =>
    setForm((f) => {
      const next = { ...f.memberships };
      delete next[tid];
      return { ...f, memberships: next };
    });

  const setMembershipRole = (tid, role) =>
    setForm((f) => ({ ...f, memberships: { ...f.memberships, [tid]: role } }));

  const assignedTenants = useMemo(
    () => tenants.filter((t) => t.id in form.memberships),
    [tenants, form.memberships]
  );
  const availableTenants = useMemo(
    () => tenants.filter((t) => !(t.id in form.memberships)),
    [tenants, form.memberships]
  );

  const save = async (e) => {
    e.preventDefault();
    const memberships = Object.entries(form.memberships).map(([tenant_id, role]) => ({ tenant_id, role }));
    if (memberships.length === 0) { toast.error("Select at least one tenant"); return; }
    setBusy(true);
    try {
      if (editId) {
        const payload = { name: form.name, email: form.email };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${editId}`, payload);
        // Sync tenant memberships and per-tenant roles (add/remove/reassign sibling accounts).
        const r = await api.put(`/admin/users/${editId}/tenants`, { memberships });
        const parts = [];
        if (r.data.added?.length) parts.push(`added ${r.data.added.length}`);
        if (r.data.removed?.length) parts.push(`removed ${r.data.removed.length}`);
        if (r.data.changed?.length) parts.push(`re-roled ${r.data.changed.length}`);
        toast.success("User updated" + (parts.length ? ` — ${parts.join(", ")}` : ""));
      } else {
        const r = await api.post("/admin/users", {
          name: form.name, email: form.email, password: form.password,
          locations: [], memberships,
        });
        toast.success(`Created in ${r.data.created.length} tenant(s)` + (r.data.skipped?.length ? `, skipped ${r.data.skipped.length}` : ""));
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    try {
      for (const id of deleteId.ids) await api.delete(`/admin/users/${id}`);
      toast.success(`Deleted ${deleteId.email} from ${deleteId.ids.length} tenant(s)`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setDeleteId(null); }
  };

  const doReset2fa = async () => {
    try {
      for (const id of reset2faId.ids) await api.post(`/admin/users/${id}/2fa/reset`);
      toast.success("2FA reset — user must set it up again");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setReset2faId(null); }
  };

  const toggle2faAllowed = async (g) => {
    const next = !(g.twofa_allowed ?? true);
    try {
      for (const m of g.memberships) await api.post(`/admin/users/${m.id}/2fa/allow`, { allowed: next });
      toast.success(next ? "2FA enabled for this user" : "2FA turned off for this user");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const inputCls = "w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand";

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="admin-users-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">Super Admin</div>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display mt-1">User Administration</h1>
          <p className="text-zinc-500 mt-1 text-sm">Manage users across all tenants, including their two-factor security.</p>
        </div>
        <button
          onClick={openCreate}
          data-testid="admin-add-user-button"
          className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5 flex items-center gap-2"
        >
          <Plus size={18} /> New User
        </button>
      </div>

      {/* Super Admin accounts */}
      <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6 mb-8" data-testid="superadmin-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Crown size={20} className="text-brand-display" />
            <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-900">Super Admin Accounts</h2>
          </div>
          <button
            onClick={openSaCreate}
            data-testid="add-superadmin-button"
            className="border border-brand text-brand rounded-full font-bold tracking-tight hover:bg-brand-card-blue transition-colors px-4 py-2 flex items-center gap-2 text-sm"
          >
            <Plus size={16} /> Add Super Admin
          </button>
        </div>
        <div className="divide-y divide-zinc-100">
          {supers.map((a) => (
            <div key={a.id} data-testid={`superadmin-row-${a.id}`} className="flex items-center justify-between py-3">
              <div>
                <div className="font-semibold text-zinc-900 flex items-center gap-2">
                  {a.name}
                  {a.is_self && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-brand-card-sage text-zinc-800">You</span>}
                </div>
                <div className="text-sm text-zinc-500">{a.email}</div>
              </div>
              <div className="flex items-center gap-3">
                {a.twofa_enabled ? (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700" data-testid={`superadmin-2fa-on-${a.id}`}>
                    <ShieldCheck size={14} /> {a.twofa_method === "totp" ? "App" : "Email"}
                  </span>
                ) : (
                  <span className="text-xs text-zinc-400">2FA off</span>
                )}
                {a.twofa_enabled && (
                  <button onClick={() => setSaReset2faId(a.id)} title="Reset 2FA" data-testid={`reset-superadmin-2fa-${a.id}`} className="text-zinc-400 hover:text-amber-600"><ShieldOff size={16} /></button>
                )}
                <button onClick={() => openSaEdit(a)} data-testid={`edit-superadmin-${a.id}`} className="text-zinc-400 hover:text-brand"><Pencil size={16} /></button>
                {!a.is_self && (
                  <button onClick={() => setSaDeleteId(a.id)} data-testid={`delete-superadmin-${a.id}`} className="text-zinc-400 hover:text-red-600"><Trash2 size={16} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-900 mb-3">Tenant Users</h2>

      <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              {["Name", "Email", "Tenants", "Role", "2FA", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {grouped.map((g) => (
              <tr key={g.email} data-testid={`admin-user-row-${g.id}`} className="hover:bg-zinc-50">
                <td className="px-4 py-3 font-semibold text-zinc-900">{g.name}</td>
                <td className="px-4 py-3 text-zinc-600">{g.email}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-1.5" data-testid={`admin-user-tenants-${g.id}`}>
                    <span
                      className="text-[11px] font-bold px-2 py-1 rounded-full bg-brand text-white"
                      title={g.memberships.map((m) => m.tenant_name).join(", ")}
                      data-testid={`admin-user-tenant-count-${g.id}`}
                    >
                      {g.memberships.length === 1 ? "1 tenant" : `${g.memberships.length} tenants`}
                    </span>
                    {g.memberships.map((m) => (
                      <span key={m.id} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full bg-brand-card-blue text-zinc-800">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tenants.find((x) => x.id === m.tenant_id)?.color || tenantColor(m.tenant_id) }} />
                        {m.tenant_name} · <span className="capitalize font-semibold">{m.role.replace("_", " ")}</span>
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-600 capitalize">
                  {(() => {
                    const roles = Array.from(new Set(g.memberships.map((m) => m.role)));
                    return roles.length === 1 ? roles[0].replace("_", " ")
                      : <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 not-italic" title={roles.map((r) => r.replace("_", " ")).join(", ")}>Mixed</span>;
                  })()}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1.5">
                    <div>
                      {g.twofa_enabled ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700" data-testid={`admin-2fa-on-${g.id}`}>
                          <ShieldCheck size={14} /> {g.twofa_method === "totp" ? "App" : "Email"}
                        </span>
                      ) : g.twofa_required ? (
                        <span className="text-xs text-zinc-400">Off (req)</span>
                      ) : (g.twofa_allowed ?? true) ? (
                        <span className="text-xs text-zinc-400">Off</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600" data-testid={`admin-2fa-blocked-${g.id}`}>
                          <ShieldOff size={14} /> Turned off
                        </span>
                      )}
                    </div>
                    {!g.twofa_required && (
                      <label className="flex items-center gap-2 cursor-pointer w-fit">
                        <Switch
                          checked={g.twofa_allowed ?? true}
                          onCheckedChange={() => toggle2faAllowed(g)}
                          data-testid={`admin-2fa-allow-${g.id}`}
                        />
                        <span className="text-[11px] text-zinc-500">Allow 2FA</span>
                      </label>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 justify-end">
                    {g.twofa_enabled && (
                      <button onClick={() => setReset2faId({ ids: g.memberships.map((m) => m.id), email: g.email })} title="Reset 2FA" data-testid={`admin-reset-2fa-${g.id}`} className="text-zinc-400 hover:text-amber-600">
                        <ShieldOff size={16} />
                      </button>
                    )}
                    <button onClick={() => openEdit(g)} data-testid={`admin-edit-user-${g.id}`} className="text-zinc-400 hover:text-brand"><Pencil size={16} /></button>
                    <button onClick={() => setDeleteId({ ids: g.memberships.map((m) => m.id), email: g.email })} data-testid={`admin-delete-user-${g.id}`} className="text-zinc-400 hover:text-red-600"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {grouped.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-400">No users yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
              {editId ? "Edit User" : "New User"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-user-name" className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">
                Email {editId && <span className="text-zinc-400 font-normal">(applies to all this user's tenants)</span>}
              </label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-user-email" className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">
                Password {editId && <span className="text-zinc-400 font-normal">(leave blank to keep)</span>}
              </label>
              <input type="password" required={!editId} minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-user-password" className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">
                Tenant access &amp; role
                <span className="text-zinc-400 font-normal"> — add tenants and set a role for each</span>
              </label>
              <div className="space-y-2" data-testid="admin-tenant-select">
                {assignedTenants.length === 0 && (
                  <div className="border border-dashed border-zinc-200 rounded-lg px-3 py-4 text-sm text-zinc-400 text-center">
                    No tenants assigned yet. Use the dropdown below to add one.
                  </div>
                )}
                {assignedTenants.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-lg" data-testid={`admin-tenant-row-${t.id}`}>
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: t.color || tenantColor(t.id) }} />
                    <span className="flex-1 text-sm text-zinc-800 truncate">{t.name}</span>
                    <select
                      value={form.memberships[t.id] || "manager"}
                      onChange={(e) => setMembershipRole(t.id, e.target.value)}
                      data-testid={`admin-tenant-role-${t.id}`}
                      className="text-xs px-2 py-1.5 border border-zinc-300 rounded bg-white capitalize focus:outline-none focus:ring-2 focus:ring-brand"
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{ROLE_META[r]?.label || r}</option>)}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeTenant(t.id)}
                      data-testid={`admin-tenant-remove-${t.id}`}
                      className="text-zinc-400 hover:text-red-600 shrink-0"
                      title="Remove tenant"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                {availableTenants.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => e.target.value && addTenant(e.target.value)}
                    data-testid="admin-tenant-add"
                    className="w-full text-sm px-3 py-2 border border-zinc-300 rounded-lg bg-white text-zinc-600 focus:outline-none focus:ring-2 focus:ring-brand"
                  >
                    <option value="">+ Add tenant…</option>
                    {availableTenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
                {tenants.length === 0 && <div className="px-3 py-3 text-sm text-zinc-400">No tenants available.</div>}
              </div>
            </div>
            <DialogFooter>
              <button type="submit" disabled={busy} data-testid="admin-user-save" className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-6 py-2.5 flex items-center gap-2 disabled:opacity-60">
                {busy && <Loader2 className="animate-spin" size={16} />} {editId ? "Save" : "Create"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes <b>{deleteId?.email}</b> from {deleteId?.ids?.length || 0} tenant(s). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="admin-confirm-delete" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!reset2faId} onOpenChange={(o) => !o && setReset2faId(null)}>
        <AlertDialogContent className="rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset two-factor authentication?</AlertDialogTitle>
            <AlertDialogDescription>The user's current 2FA will be removed. They'll be prompted to set it up again at next login (if required for their role).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doReset2fa} data-testid="admin-confirm-reset-2fa" className="bg-amber-600 hover:bg-amber-700">Reset 2FA</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!saDeleteId} onOpenChange={(o) => !o && setSaDeleteId(null)}>
        <AlertDialogContent className="rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this super admin?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the super admin account. At least one super admin must remain.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doSaDelete} data-testid="confirm-delete-superadmin" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!saReset2faId} onOpenChange={(o) => !o && setSaReset2faId(null)}>
        <AlertDialogContent className="rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset this super admin's 2FA?</AlertDialogTitle>
            <AlertDialogDescription>Their two-factor authentication will be removed. Because 2FA is mandatory for super admins, they'll be prompted to set it up again at next login.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doSaReset2fa} data-testid="confirm-reset-superadmin-2fa" className="bg-amber-600 hover:bg-amber-700">Reset 2FA</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={saOpen} onOpenChange={setSaOpen}>
        <DialogContent className="rounded-lg max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
              {saEditId ? "Edit Super Admin" : "New Super Admin"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveSa} className="space-y-4">
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Name</label>
              <input required value={saForm.name} onChange={(e) => setSaForm({ ...saForm, name: e.target.value })} data-testid="sa-name" className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Email</label>
              <input type="email" required value={saForm.email} onChange={(e) => setSaForm({ ...saForm, email: e.target.value })} data-testid="sa-email" className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">
                Password {saEditId && <span className="text-zinc-400 font-normal">(leave blank to keep)</span>}
              </label>
              <input type="password" required={!saEditId} minLength={6} value={saForm.password} onChange={(e) => setSaForm({ ...saForm, password: e.target.value })} data-testid="sa-password" className={inputCls} />
            </div>
            <DialogFooter>
              <button type="submit" disabled={saBusy} data-testid="sa-save" className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-6 py-2.5 flex items-center gap-2 disabled:opacity-60">
                {saBusy && <Loader2 className="animate-spin" size={16} />} {saEditId ? "Save" : "Create"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
